import React, { createContext, useState, useContext, ReactNode, Dispatch, SetStateAction, useEffect } from 'react';

interface TimeWindowOption {
  key: string;
  label: string;
}

interface PortfolioViewContextType {
  timeWindowOptions: TimeWindowOption[];
  selectedTimeWindow: TimeWindowOption;
  setSelectedTimeWindow: Dispatch<SetStateAction<TimeWindowOption>>;
  showRealizedGains: boolean;
  setShowRealizedGains: Dispatch<SetStateAction<boolean>>;
  expandedAccounts: Set<string>;
  setExpandedAccounts: Dispatch<SetStateAction<Set<string>>>;
  toggleAccountExpansion: (accountId: string) => void;
}

const timeWindowOptionsData: TimeWindowOption[] = [
  // { key: '1d', label: '1 day' },     // Commented out - no backend support yet
  // { key: '1w', label: '1 week' },    // Commented out - no backend support yet
  { key: '1m', label: '1 month' },
  { key: '3m', label: '3 months' },
  { key: '6m', label: '6 months' },
  { key: '1y', label: '1 year' },
  { key: '2y', label: '2 years' },
];

const PortfolioViewContext = createContext<PortfolioViewContextType | undefined>(undefined);
const LOCALSTORAGE_SHOW_REALIZED_GAINS = 'dashboard_showRealizedGains';

interface PortfolioViewProviderProps {
  children: ReactNode;
}

export const PortfolioViewProvider: React.FC<PortfolioViewProviderProps> = ({ children }) => {
  const [selectedTimeWindow, setSelectedTimeWindow] = useState<TimeWindowOption>(() => {
    try {
      const saved = localStorage.getItem('dashboard_selectedTimeWindowKey');
      if (saved) {
        const found = timeWindowOptionsData.find(option => option.key === saved);
        if (found) {
          return found;
        }
      }
    } catch (error) {
      console.error('Error loading selected time window from localStorage:', error);
    }
    return timeWindowOptionsData[2]; // Default to 6 months if no saved value
  });

  const [showRealizedGains, setShowRealizedGains] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      const saved = localStorage.getItem(LOCALSTORAGE_SHOW_REALIZED_GAINS);
      if (saved !== null) {
        return saved === 'true';
      }
    } catch (error) {
      console.error('Error loading show realized gains from localStorage:', error);
    }
    return true;
  });
  
  // Initialize expandedAccounts from localStorage
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('expandedAccounts');
      if (saved) {
        const accountIds = JSON.parse(saved);
        return new Set(accountIds);
      }
    } catch (error) {
      console.error('Error loading expanded accounts from localStorage:', error);
    }
    return new Set();
  });

  // Save expandedAccounts to localStorage whenever it changes
  useEffect(() => {
    try {
      const accountIds = Array.from(expandedAccounts);
      localStorage.setItem('expandedAccounts', JSON.stringify(accountIds));
    } catch (error) {
      console.error('Error saving expanded accounts to localStorage:', error);
    }
  }, [expandedAccounts]);

  // Save selectedTimeWindow to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('dashboard_selectedTimeWindowKey', selectedTimeWindow.key);
    } catch (error) {
      console.error('Error saving selected time window to localStorage:', error);
    }
  }, [selectedTimeWindow.key]);

  useEffect(() => {
    try {
      localStorage.setItem(LOCALSTORAGE_SHOW_REALIZED_GAINS, String(showRealizedGains));
    } catch (error) {
      console.error('Error saving show realized gains to localStorage:', error);
    }
  }, [showRealizedGains]);

  const toggleAccountExpansion = (accountId: string) => {
    setExpandedAccounts(prevExpanded => {
      const newExpanded = new Set(prevExpanded);
      if (newExpanded.has(accountId)) {
        newExpanded.delete(accountId);
      } else {
        newExpanded.add(accountId);
      }
      return newExpanded;
    });
  };

  const value = {
    timeWindowOptions: timeWindowOptionsData,
    selectedTimeWindow,
    setSelectedTimeWindow,
    showRealizedGains,
    setShowRealizedGains,
    expandedAccounts,
    setExpandedAccounts,
    toggleAccountExpansion
  };

  return (
    <PortfolioViewContext.Provider value={value}>
      {children}
    </PortfolioViewContext.Provider>
  );
};

export const usePortfolioView = (): PortfolioViewContextType => {
  const context = useContext(PortfolioViewContext);
  if (context === undefined) {
    throw new Error('usePortfolioView must be used within a PortfolioViewProvider');
  }
  return context;
};

// Legacy exports for backward compatibility during migration
export const useDashboard = usePortfolioView;
export const DashboardProvider = PortfolioViewProvider; 
